// Query keys — the one place the cache's vocabulary lives (ARCH §8.1, §9.3; the T-022 → T-023 Inbox note).
//
// Scheme: everything that belongs to an account sits under `['account', accountId]`, so "Reset demo" / "Start
// fresh" / a refresh can drop the whole account with one prefix; the two user-scoped reads (`session`,
// `preferences`) sit at the root. Period-keyed reads end in a plain object `{ since, until, compare, … }` —
// TanStack hashes objects with sorted keys, so property order never matters, and `undefined` is normalised
// away here (`compare ?? 'previous'`, `entityId ?? null`) so two spellings of the same query share one entry.
// The family prefixes (`entities`, `spend`, `changes`, …) are reserved now so the invalidation map can name
// them before their factories exist (T-025 …).

import type { PeriodQuery } from '../api/ApiClient';
import type { AlertClass, AlertRuleInput, AlertState } from '../types/alerts';
import type { Placement } from '../types/creatives';
import type { EntityLevel } from '../types/entities';

/** API §9.4's list filters as a screen holds them — `view` is the factory's (`alertInbox` / `alertHistory`), `cursor` the page's (T-167). */
export interface AlertListFilters {
  class?: AlertClass;
  state?: AlertState;
  ruleId?: string;
  entityId?: string;
  limit?: number;
}

export interface AlertListKey {
  view: 'inbox' | 'history';
  class: AlertClass | null;
  state: AlertState | null;
  ruleId: string | null;
  entityId: string | null;
  limit: number | null;
}

/** Every spelling of one filter set collapses to one key (`undefined` → `null`); the cursor is never part of it (the log's rule). */
export function alertListKey(view: 'inbox' | 'history', q: AlertListFilters): AlertListKey {
  return {
    view,
    class: q.class ?? null,
    state: q.state ?? null,
    ruleId: q.ruleId ?? null,
    entityId: q.entityId ?? null,
    limit: q.limit ?? null,
  };
}

/** The part of a rule draft the back-test reads (D-239: never `name`, never `enabled`), in one canonical spelling (T-166). */
export interface BacktestDraftKey {
  metric: AlertRuleInput['metric'];
  scope: AlertRuleInput['scope'];
  condition: AlertRuleInput['condition'];
  window: AlertRuleInput['window'];
  class: AlertRuleInput['class'] | null;
  minSpend: number | null;
  includeLearning: boolean | null;
}

/** Every spelling of one draft collapses to one key: sorted entity ids, optional fields as `null` (T-166). */
export function backtestDraftKey(draft: AlertRuleInput): BacktestDraftKey {
  const scope =
    draft.scope.kind === 'entities'
      ? { kind: 'entities' as const, entityIds: [...draft.scope.entityIds].sort() }
      : draft.scope;
  return {
    metric: draft.metric,
    scope,
    condition: draft.condition,
    window: draft.window,
    class: draft.class ?? null,
    minSpend: draft.minSpend ?? null,
    includeLearning: draft.includeLearning ?? null,
  };
}

export interface PeriodKey {
  since: string;
  until: string;
  compare: 'previous' | 'none';
}

/** `{ since, until, compare }` with the wire default made explicit (API §1.5: compare is `previous` unless `none`). */
export function periodKey(q: PeriodQuery): PeriodKey {
  return { since: q.since, until: q.until, compare: q.compare ?? 'previous' };
}

export type TimeseriesQuery = PeriodQuery & { entityId?: string; granularity?: 'day' | 'hour' };
export type EntitiesQuery = PeriodQuery & { level: EntityLevel; parentId?: string };
export type PacingQuery = { month?: string; withStaged?: boolean };
export type AllocationQuery = PeriodQuery & { basis?: 'results' | 'value' };
export type CreativesQuery = PeriodQuery & { campaignId?: string; adSetId?: string };

/** API §7.1's filters, as `ApiClient.getActivity` takes them (T-126). `cursor` is deliberately NOT part of the
 *  key: a cursor is a position within one answer, and a page-2 read must not be a different cached question
 *  from page 1 — `useInfiniteQuery` walks the pages under the one key. */
export type ActivityQuery = {
  entityId?: string;
  includeDescendants?: boolean;
  kind?: string;
  source?: string;
  userId?: string;
  since?: string;
  until?: string;
  limit?: number;
};

export interface ActivityKey {
  entityId: string | null;
  includeDescendants: boolean;
  kind: string | null;
  source: string | null;
  userId: string | null;
  since: string | null;
  until: string | null;
  limit: number | null;
}

/** Every spelling of the same filter set collapses to one key (`undefined` → `null`, the flag → a boolean). */
export function normaliseActivityQuery(q: ActivityQuery): ActivityKey {
  return {
    entityId: q.entityId ?? null,
    includeDescendants: q.includeDescendants === true,
    kind: q.kind ?? null,
    source: q.source ?? null,
    userId: q.userId ?? null,
    since: q.since ?? null,
    until: q.until ?? null,
    limit: q.limit ?? null,
  };
}

export const queryKeys = {
  // user-scoped
  session: () => ['session'] as const,
  preferences: () => ['preferences'] as const,

  // the account namespace
  account: (accountId: string) => ['account', accountId] as const,
  accountInfo: (accountId: string) => ['account', accountId, 'info'] as const,
  sync: (accountId: string) => ['account', accountId, 'sync'] as const,
  faults: (accountId: string) => ['account', accountId, 'faults'] as const,

  // Home (API §3.1–3.2) — period-keyed
  summary: (accountId: string, q: PeriodQuery) =>
    ['account', accountId, 'summary', periodKey(q)] as const,
  timeseries: (accountId: string, q: TimeseriesQuery) =>
    [
      'account',
      accountId,
      'timeseries',
      {
        ...periodKey(q),
        entityId: q.entityId ?? null,
        // the day granularity is the default and stays out of the key (the Inbox note's exact shape);
        // an hourly request (V1, REQ-022 AC3) is a different entry
        ...(q.granularity === 'hour' ? { granularity: 'hour' as const } : {}),
      },
    ] as const,

  // Hierarchy (API §4) — the family prefix, then one leaf per endpoint
  entities: (accountId: string) => ['account', accountId, 'entities'] as const,
  entitiesIndex: (accountId: string) => ['account', accountId, 'entities', 'index'] as const,
  entitiesList: (accountId: string, q: EntitiesQuery) =>
    [
      'account',
      accountId,
      'entities',
      'list',
      { level: q.level, parentId: q.parentId ?? null, ...periodKey(q) },
    ] as const,
  entity: (accountId: string, entityId: string, q: PeriodQuery) =>
    ['account', accountId, 'entities', 'detail', entityId, periodKey(q)] as const,
  tree: (accountId: string, q: PeriodQuery) =>
    ['account', accountId, 'entities', 'tree', periodKey(q)] as const,
  adsManagerLink: (accountId: string, entityId: string) =>
    ['account', accountId, 'entities', 'adsManagerLink', entityId] as const,

  // Spend & budget (API §5) — the family prefix T-023 reserved, then one leaf per endpoint (T-087).
  // `spendPlan` and `spendPacing` key on a MONTH, not a period: `?month=` is what §5.1 / §5.3 take, and
  // "This month / Last month" (REQ-071 AC5) is that one parameter changing. `spendAllocation` and
  // `spendWasted` are period-keyed like every §4 read, so a period switch keeps the last map on screen.
  spend: (accountId: string) => ['account', accountId, 'spend'] as const, // plan, pacing, allocation, wasted (§5)
  spendPlan: (accountId: string, month?: string) =>
    ['account', accountId, 'spend', 'plan', { month: month ?? null }] as const,
  spendPacing: (accountId: string, q: PacingQuery = {}) =>
    [
      'account',
      accountId,
      'spend',
      'pacing',
      { month: q.month ?? null, withStaged: q.withStaged === true },
    ] as const,
  spendAllocation: (accountId: string, q: AllocationQuery) =>
    [
      'account',
      accountId,
      'spend',
      'allocation',
      { ...periodKey(q), basis: q.basis ?? 'results' },
    ] as const,
  spendWasted: (accountId: string, q: PeriodQuery) =>
    ['account', accountId, 'spend', 'wasted', periodKey(q)] as const,

  // Change-sets (API §6) — the family prefix T-023 reserved, then the open tray and its review (T-125).
  // `changePreview` keys on the selected `itemIds` because §6.5 takes them in the body: the review screen's
  // "deselect this change" (REQ-104 AC6) is a different question with a different answer, and the two must
  // not share a cache entry. `null` is "every item", which is what the tray's own Review opens with.
  changeSet: (accountId: string) => ['account', accountId, 'changes', 'current'] as const,
  changePreview: (accountId: string, itemIds?: readonly string[]) =>
    [
      'account',
      accountId,
      'changes',
      'preview',
      { itemIds: itemIds === undefined || itemIds.length === 0 ? null : [...itemIds].sort() },
    ] as const,

  // Creatives (API §8) — the gallery is period-keyed like every §4 read (its overlay is the period's figures
  // and so are its ribbons); one ad's creative and its previews are not: they do not change with the dates.
  creatives: (accountId: string) => ['account', accountId, 'creatives'] as const, // §8
  creativesList: (accountId: string, q: CreativesQuery) =>
    [
      'account',
      accountId,
      'creatives',
      'list',
      { ...periodKey(q), campaignId: q.campaignId ?? null, adSetId: q.adSetId ?? null },
    ] as const,
  creative: (accountId: string, adId: string) =>
    ['account', accountId, 'creatives', 'detail', adId] as const,
  preview: (
    accountId: string,
    adId: string,
    placement: Placement,
    options?: { interactive?: boolean },
  ) =>
    [
      'account',
      accountId,
      'creatives',
      'preview',
      adId,
      { placement, ...(options?.interactive ? { interactive: true } : {}) },
    ] as const,

  // Settings (API §10) — the family prefix T-023 reserved, then the record and one campaign's options (T-088)
  settings: (accountId: string) => ['account', accountId, 'settings'] as const, // §10 account settings
  settingsRecord: (accountId: string) => ['account', accountId, 'settings', 'record'] as const,
  resultOptions: (accountId: string, campaignId: string) =>
    ['account', accountId, 'settings', 'resultOptions', campaignId] as const,

  // Family prefixes reserved for later factories — named by the invalidation map today.
  changes: (accountId: string) => ['account', accountId, 'changes'] as const, // change-set, checklist (§5)
  // API §7.1's log is paginated and filtered, so the key carries the query — a filtered page and the whole
  // log are two different answers and must not share a cache entry. `activityList` sits UNDER the `activity`
  // family prefix, so every mutation that already invalidates `activity` invalidates every page of it (T-126).
  activity: (accountId: string) => ['account', accountId, 'activity'] as const, // §7.1 activity log
  activityList: (accountId: string, q: ActivityQuery = {}) =>
    ['account', accountId, 'activity', 'list', normaliseActivityQuery(q)] as const,
  alerts: (accountId: string) => ['account', accountId, 'alerts'] as const, // presets, rules, inbox (§9)
  // Alerts (API §9.1–9.3) — one leaf per endpoint under the family prefix (T-166). `alertBacktest` keys on the
  // DRAFT (`backtestDraftKey`), so the builder's every distinct draft is one cache entry and a repeated one
  // answers at once; `alertInbox` is the prefix T-167's §9.4 list sits under (its filters become the leaf).
  alertPresets: (accountId: string) => ['account', accountId, 'alerts', 'presets'] as const,
  alertRules: (accountId: string) => ['account', accountId, 'alerts', 'rules'] as const,
  alertRule: (accountId: string, ruleId: string) =>
    ['account', accountId, 'alerts', 'rules', ruleId] as const,
  alertRuleOptions: (accountId: string) => ['account', accountId, 'alerts', 'ruleOptions'] as const,
  alertBacktest: (accountId: string, draft: AlertRuleInput) =>
    ['account', accountId, 'alerts', 'backtest', backtestDraftKey(draft)] as const,
  // §9.4 (T-167) — the `alertInbox` family is the whole Inbox section: both views' pages and one alert's detail
  // sit under it, so a read / snooze / dismiss / act invalidates them together and nothing else under `alerts`.
  // `alertList` keys on the FILTERS and the view; the cursor stays out (`useInfiniteQuery` walks the pages).
  alertInbox: (accountId: string) => ['account', accountId, 'alerts', 'inbox'] as const,
  alertList: (accountId: string, view: 'inbox' | 'history', q: AlertListFilters = {}) =>
    ['account', accountId, 'alerts', 'inbox', 'list', alertListKey(view, q)] as const,
  alert: (accountId: string, alertId: string) =>
    ['account', accountId, 'alerts', 'inbox', 'detail', alertId] as const,
  briefing: (accountId: string) => ['account', accountId, 'briefing'] as const, // §3.3
  firstRun: (accountId: string) => ['account', accountId, 'firstRun'] as const, // §3.6
} as const;

export type QueryKeys = typeof queryKeys;
/** The families the invalidation map can name (every key builder that takes only the account id, plus the two
 *  user-scoped ones). */
export type KeyFamily =
  | 'session'
  | 'preferences'
  | 'account'
  | 'accountInfo'
  | 'sync'
  | 'faults'
  | 'entities'
  | 'spend'
  /** `['account', id, 'spend', 'pacing']` — both `withStaged` variants of §5.3 and no other spend read.
   *  Staging moves the staged forecast (REQ-072 AC3) and nothing else under `spend` (T-125). */
  | 'spendPacing'
  | 'changes'
  | 'activity'
  | 'creatives'
  | 'alerts'
  /** The three sub-families of §9 (T-166): the gallery, the rules (list + one), the inbox — both views and the
   *  detail (T-167). A rule write invalidates the whole `alerts` family — it is evaluated at once and the inbox
   *  may have moved; an inbox write invalidates `alertInbox` (and `alertRules` when it touched a snooze). */
  | 'alertPresets'
  | 'alertRules'
  | 'alertInbox'
  | 'briefing'
  | 'firstRun'
  | 'settings'
  | 'summary'
  | 'timeseries';

/** The key prefix of a family for an account — `summary` / `timeseries` are the period-less prefixes that match
 *  every period of that read. */
export function familyKey(family: KeyFamily, accountId: string): readonly unknown[] {
  switch (family) {
    case 'session':
      return queryKeys.session();
    case 'preferences':
      return queryKeys.preferences();
    case 'summary':
      return ['account', accountId, 'summary'];
    case 'timeseries':
      return ['account', accountId, 'timeseries'];
    case 'spendPacing':
      return ['account', accountId, 'spend', 'pacing'];
    default:
      return queryKeys[family](accountId);
  }
}
